import {find, isEmpty, each} from 'lodash-es'

export default {
  onlineStatus: state => state.onlineStatus,
  allAttributes: state => state.allAttributes,
  config: state => state.config,
  getCartProductById: (state) => (productId) => {
    return state.cart.liveData.find(item => item.productId === productId) || {}
  },
  getProductById: (state) => (productId) => {
    return state.products.find(item => item.productId === productId) || JSON.parse(JSON.stringify(state.emptyProduct))
  },
  getProductBadgeImage: (state) => (productId) => {
    return state.productBadges.find(item => item.id === productId)
  },
  getAttributeIdsOfGroup: (state) => (groupId) => {
    const group = state.allAttributes.find(item => item.attributeGroupId === groupId)

    return group.attributes.reduce((acc, item) => {
      acc.push(item.attributeId)
      return acc
    }, [])
  },
  getSelectedProductByCodeId: (state) => (productCodeId) => {
    if (!productCodeId) {
      return JSON.parse(JSON.stringify(state.emptyProduct))
    }

    const productData = state.products.find(item => {
      return !!item.productCodes.find(subItem => {
        return subItem.productCodeId === productCodeId
      })
    })

    if (!productData) {
      return JSON.parse(JSON.stringify(state.emptyProduct))
    }

    return productData
  },
  getSelectedProductCodeByCodeId: (state) => (productCodeId) => {
    if (!productCodeId) {
      return JSON.parse(JSON.stringify(state.emptyObject))
    }

    const productData = state.products.find(item => {
      return !!item.productCodes.find(subItem => {
        return subItem.productCodeId === productCodeId
      })
    })

    if (!productData) {
      return JSON.parse(JSON.stringify(state.emptyObject))
    }

    return productData.productCodes.find(item => item.productCodeId === productCodeId)
  },
  getAttributeIdsOfGroupByAttributeId: (state) => (attributeId) => {
    // find the attribute group
    const group = state.allAttributes.find(item => {
      return item.attributes.find(sub => sub.attributeId === attributeId)
    })

    if (group) {
      return group.attributes.reduce((acc, item) => {
        acc.push(item.attributeId)
        return acc
      }, [])
    }

    return []
  },
  getOtherAttributeIdsOfGroup: (state, getters) => (attributeId) => {
    const groupAttributeIds = getters.getAttributeIdsOfGroupByAttributeId(attributeId)

    return groupAttributeIds.reduce((acc, item) => {
      if (item !== attributeId) {
        acc.push(item)
      }
      return acc
    }, [])
  },
  getProductCodeWithAttributesMap: (state, getters) => (productId) => {
    const product = getters.getProductById(productId)
    return product.productCodes.reduce((acc, item) => {
      if (item.productActive || getters.config.showInactiveProductCodes) {
        acc.push({
          productCodeId: item.productCodeId,
          attributes: item.productCodeAttributes
        })
      }
      return acc
    }, [])
  },
  getUniqueProductAttributeValueIds: (state, getters) => (productId) => {
    const productData = getters.getProductById(productId)

    const attrValueIds = productData.productCodes.reduce((acc, item) => {
      if (item.productActive || getters.config.showInactiveProductCodes) {
        return acc.concat(item.productCodeAttributes)
      }
      return acc
    }, [])

    return attrValueIds.sort().reduce((acc, item) => {
      const length = acc.length
      if (length === 0 || acc[length - 1] !== item) {
        acc.push(item)
      }
      return acc
    }, [])
  },
  getUsedAttributeValuesByGroup: (state, getters) => (productId) => {
    const uniqueAttrValueIds = getters.getUniqueProductAttributeValueIds(productId)

    const selectedFilters = state.allAttributes.filter(item => {
      return item.attributes.filter(sub => {
        return uniqueAttrValueIds.includes(sub.attributeId)
      }).length > 0
    })

    return selectedFilters.map(item => {
      const attributes = item.attributes.filter(sub => {
        return uniqueAttrValueIds.includes(sub.attributeId)
      })

      const returnItem = JSON.parse(JSON.stringify(item))
      returnItem.attributes = attributes
      return returnItem
    })
  },
  noImage: state => state.noImage,
  cartContents: state => state.cart.cartContents,
  giftContents: (state) => state.cart.giftContents,
  giftChoicesGroupedByGiftId: (state, getters) => {
    if (Object.keys(getters.giftRules).length < 1) {
      return {}
    }
    const productIdsGroupedByRuleIdsForSelection = {}
    each(getters.giftRules, (giftRule) => {
      productIdsGroupedByRuleIdsForSelection[giftRule.id] = giftRule
    })
    return productIdsGroupedByRuleIdsForSelection
  },
  giftRules: state => state.cart.giftRules,
  cartProductsCount: state => state.cart.cartContents.length,
  cartProductPiecesCount: state => {
    return state.cart.cartContents.reduce((acc, item) => {
      const product = state.products.find(prod => prod.productId === item.productId)
      if (product) {
        acc += item.quantity
      }
      return acc
    }, 0)
  },
  cartTotalWithDiscount: (state, getters) => {
    return getters.cartProductsTotal - getters.orderCouponDiscount
  },
  cartEmpty: state => state.cart.cartContents.length === 0,
  cartProductsTotal: state => {
    return state.cart.cartContents.reduce((acc, item) => {
      const product = state.products.find(prod => prod.productId === item.productId)
      if (product) {
        acc += product.productFinalPrice * item.quantity
      }
      return acc
    }, 0)
  },
  cartProductWeightTotal: state => {
    return state.cart.cartContents.reduce((acc, item) => {
      const product = state.products.find(prod => prod.productId === item.productId)
      if (product) {
        acc += product.productWeight * item.quantity
      }
      return acc
    }, 0)
  },
  cartProductCodeQuantity: (state) => (productCodeId, incrementOverride = 1) => {
    const cartItem = state.cart.cartContents.find(item => Number(item.productCodeId) === Number(productCodeId))
    const cartItemQuantity = cartItem ? cartItem.quantity : 0
    return cartItemQuantity + incrementOverride
  },
  cartProductWithCustomizationQuantity: (state, getters) => (
    productCodeId,
    customizations = null,
    incrementOverride = 1
  ) => {
    if (null === customizations) {
      return getters.cartProductCodeQuantity(productCodeId, incrementOverride)
    }
    const cartItem = state.cart.cartContents.find(item => {
      if (!('cartProductCustomizations' in item.options)) {
        return false
      }
      if (!item.options.cartProductCustomizations === customizations) {
        return false
      }
      return item.productCodeId === productCodeId
    })
    const cartItemQuantity = cartItem ? cartItem.quantity : 0
    return cartItemQuantity + incrementOverride
  },
  cartSpinnerQuantity: (state) => (productCodeId) => {
    const cartItem = state.cart.cartContents.find(item => item.productCodeId === productCodeId)
    return cartItem ? cartItem.quantity : 0
  },
  cartProductError: (state) => (productCodeId) => state.cart.cartErrors.find(item => item.productCodeId === productCodeId),
  cartProductErrorMsg: (state) => (productCodeId) => {
    const error = state.cart.cartErrors.find(item => item.productCodeId === productCodeId)
    return error ? error.msg : ''
  },
  attributeName: (state) => (attributeId) => {
    return state.allAttributes.reduce((acc, item) => {
      const attribute = item.attributes.find(sub => sub.attributeId === attributeId)
      if (attribute) {
        acc = attribute.attributeName
      }
      return acc
    }, '')
  },
  productCardModal: state => state.productCardModal,
  getSelectedProductCodeId: state => state.selectedProductCodeId,
  getSelectedAttributeId: state => state.selectedAttributeId,
  getSelectedGifts: state => state.selectedGifts,
  getSelectedGiftItems: (state) => {
    const selectedGifts = state.selectedGifts
    const giftItems = {}
    for (const giftId in selectedGifts) {
      const selectedGiftsEntry = selectedGifts[giftId]
      for (const selectedGiftItemIdx in selectedGiftsEntry) {
        const selectedGiftItem = selectedGiftsEntry[selectedGiftItemIdx]
        if (!giftItems.hasOwnProperty(selectedGiftItem)) {
          giftItems[selectedGiftItem.productId] = {
            productId: selectedGiftItem.productId,
            quantity: 0
          }
        }
        giftItems[selectedGiftItem.productId].quantity += Number(selectedGiftItem.quantity)
      }
    }
    return giftItems
  },
  getSelectedGiftItemsForReactiveFormManipulator: (state, getters) => {
    const selectedGiftItems = getters.getSelectedGiftItems
    const reactiveFormInputKeyValuePair = []
    for (const productId in selectedGiftItems) {
      const giftItemEntry = selectedGiftItems[productId]
      reactiveFormInputKeyValuePair.push({
        name: `user_gifts[products][${giftItemEntry.productId}]`,
        value: giftItemEntry.productId
      })
      reactiveFormInputKeyValuePair.push({
        name: `user_gifts[quantities][${giftItemEntry.productId}]`,
        value: giftItemEntry.quantity
      })
    }
    return reactiveFormInputKeyValuePair
  },
  findGiftIdsForProductId: (state) => (productId) => {
    const productRequirementOptionType = 1
    const foundGiftIds = []
    for (const giftId in state.cart.giftRules) {
      const giftRule = state.cart.giftRules[giftId]
      if (giftRule.choices[productRequirementOptionType].map(Number).includes(productId)) {
        foundGiftIds.push(giftId)
      }
    }
    return foundGiftIds
  },
  findCartProductFromProductIdAndGift: (state) => ({ productId }) => {
    const foundGiftContentsIdx = state.cart.giftContents.findIndex((giftContent) =>
      Number(giftContent.productId) === Number(productId)
    )
    if (foundGiftContentsIdx === -1) {
      return {}
    }
    return state.cart.giftContents[foundGiftContentsIdx]
  },
  isGiftChoiceSelectionNecessary: (state, getters) => {
    for (const giftId in state.cart.giftRules) {
      if (getters.shouldGiftRuleHaveSelection(giftId)) {
        return true
      }
    }
    return false
  },
  giftUserSelectionStatus: (state, getters) => {
    const messages = {
      valid: { invalid: false, statusKey: 'valid' },
      valid_selection: { invalid: false, statusKey: 'valid_selection' },
      no_selection: { invalid: true, statusKey: 'no_selection' },
      incomplete_selection: { invalid: true, statusKey: 'incomplete_selection' },
      invalid_selection: { invalid: true, statusKey: 'invalid_selection' }
    }
    if (!getters.isGiftChoiceSelectionNecessary) {
      return messages.valid
    }
    const giftSelections = getters.getSelectedGifts
    const giftRules = getters.giftRules
    if ((Object.keys(giftSelections)).length < 1) {
      return messages.no_selection
    }
    for (const giftId in giftRules) {
      const giftRule = giftRules[giftId]
      if (!getters.shouldGiftRuleHaveSelection(giftId)) {
        continue
      }
      if (!giftSelections.hasOwnProperty(giftId)) {
        return messages.no_selection
      }
      const giftSelectionsCount = Object.keys(giftSelections[giftId]).length
      if (giftSelectionsCount > Number(giftRule.gift_user_choice_count)) {
        return messages.invalid_selection
      }
      if (giftSelectionsCount < Number(giftRule.gift_user_choice_count)) {
        return messages.incomplete_selection
      }
    }
    return messages.valid_selection
  },
  shouldGiftRuleHaveSelection: (state) => (giftId) => {
    if (!state.cart.giftRules.hasOwnProperty(giftId)) {
      return false
    }
    const giftRule = state.cart.giftRules[giftId]
    const productReqOptionType = 1
    if (!giftRule.choices.hasOwnProperty(productReqOptionType)) {
      return false
    }
    if (Number(giftRule.gift_user_choice_count) >= giftRule.choices[productReqOptionType].length) {
      return false
    }
    return Number(giftRule.gift_user_choice_count) > 0
  },
  getTransporters: (state, getters) => {
    const selectedPayWay = getters.previewOrderData.payWay
    if (selectedPayWay === 'delivery') {
      return state.transporters.filter(transporter =>
        transporter.class_name !== 'SPEEDEX' && transporter.class_name !== 'BOXNOW'
      )
    }
    return state.transporters
  },
  previewOrderData: state => state.previewOrderData,
  orderCouponCode: state => state.previewOrderData.couponCode,
  orderCouponDiscount: state => state.previewOrderData.couponDiscount,
  getSelectedTransporterId: state => state.selectedTransporterId,

  getAllTransporterSmartPoints: state => state.allTransporterSmartPoints,
  getSmartPointLoading: state => state.smartPointLoading,
  getSmartPointIsSelected: state => state.smartPointIsSelected,
  getSelectedTransporterSmartPointEnable: (state, getters) => {
    const transporterId = getters.getSelectedTransporterId
    const transporters = getters.getTransporters

    const selectedTransporter = find(transporters, (transporter) => {
      return transporter.transporter_id === transporterId
    })

    if (!isEmpty(selectedTransporter)) {
      if (selectedTransporter.deliveryOptionType === undefined) {
        return false
      }
      switch (selectedTransporter.deliveryOptionType) {
        case '0':
          return true
        case '1':
          return false
        case '2':
          return true
      }
    }

    return false
  },
  getSmartPointsEnableForTransporter: (state, getters) => (transporter) => {
    if (!transporter || !getters.getSelectedTransporterObject) {
      return false
    }
    if (transporter.id !== getters.getSelectedTransporterObject.id) {
      return false
    }
    if (transporter.deliveryOptionType === undefined) {
      return false
    }
    switch (transporter.deliveryOptionType) {
      case '0':
        return true
      case '1':
        return false
      case '2':
        return true
    }

    return false
  },
  // Delivery options : 0) All, 1) Delivery to my place, 2) Pick up from selling point
  // Default option is '1'
  getSelectedTransporterDeliveryOptionType: (state, getters) => {
    const transporterId = getters.getSelectedTransporterId
    const transporters = getters.getTransporters

    const selectedTransporter = find(transporters, (transporter) => {
      return transporter.transporter_id === transporterId
    })

    if (!isEmpty(selectedTransporter)) {
      if (selectedTransporter.deliveryOptionType === undefined || selectedTransporter.deliveryOptionType === null) {
        return '1'
      }
      return selectedTransporter.deliveryOptionType
    }

    return '1'
  },

  getSelectedTransporterDeliveryOption: (state, getters) => {
    const transporterId = getters.getSelectedTransporterId
    const transporters = getters.getTransporters

    const selectedTransporter = find(transporters, (transporter) => {
      return transporter.transporter_id === transporterId
    })

    if (!isEmpty(selectedTransporter)) {
      return Boolean(selectedTransporter.deliveryOption)
    }

    return false
  },
  getIsSmartPointOnlyTransporter: (state, getters) => {
    return getters.getSelectedTransporterDeliveryOptionType === '2'
  },
  getIsSmartPointAllOptionsTransporter: (state, getters) => {
    return getters.getSelectedTransporterDeliveryOptionType === '0'
  },
  getSelectedTransporterClassName: (state, getters) => {
    const transporterId = getters.getSelectedTransporterId
    const transporters = getters.getTransporters
    const selectedTransporter = find(transporters, (transporter) => {
      return transporter.transporter_id === transporterId
    })
    if (!isEmpty(selectedTransporter)) {
      return selectedTransporter.class_name
    }
    return ''
  },
  getCartProductsTotalPoints (state, getters) {
    return getters.cartContents.reduce((acc, cartItem) => {
      const product = state.products.find(item => item.productId === cartItem.productId)
      if (product) {
        acc += product.productPoints * cartItem.quantity
      }
      return acc
    }, 0)
  },
  getDefaultCouponCode: state => state.cart.defaultCoupon.couponCode,
  getDefaultCouponDiscount: state => state.cart.defaultCoupon.couponDiscount,
  getDefaultCouponName: state => state.cart.defaultCoupon.couponName,
  getUriWithoutOffset: state => state.uriWithoutOffset,
  getIsTopLevelCategory: state => state.isTopLevelCategory,
  getCurrentPageNumber: state => state.currentPageNumber,
  getProductCategoryData: state => state.productCategoryData,
  getSelectedTransporterSmartPoints: (state, getters) => {
    const transporterClassName = getters.getSelectedTransporterClassName
    const data = getters.getAllTransporterSmartPoints[transporterClassName]
    if (data === undefined) {
      return []
    }
    const smartPointsForSelectedTransporter = getters.getAllTransporterSmartPoints[transporterClassName].smartPointStationData
    const selectedTransporterId = getters.getSelectedTransporterId
    const availableTransporters = getters.getTransporters
    const selectedTransporter = find(availableTransporters, (transporter) => {
      return Number(transporter.transporter_id) === Number(selectedTransporterId)
    })
    if (selectedTransporter === undefined) {
      return []
    }
    const selectedTransporterClassName = selectedTransporter.class_name
    return getters.getSmartPointModelTransformed(smartPointsForSelectedTransporter, selectedTransporterClassName)
  },

  // Transform smart point data into new SmartPointModelDTO() to be used in the smart point selector use case
  getSmartPointModelTransformed: () => (smartPointsForSelectedTransporter, selectedTransporterClassName) => {
    switch (selectedTransporterClassName) {
      case 'ACS':
        return smartPointsForSelectedTransporter
      case 'BOXNOW':
        return smartPointsForSelectedTransporter
      default:
        return smartPointsForSelectedTransporter
    }
  },

  getSearchInputSmartPoints: state => state.searchInputSmartPoints,
  getMaxResultsSmartPoints: state => state.maxResultsSmartPoints,
  getClosestSmartPoints: state => state.closestSmartPoints,
  getCachedFilteredSearchedClosestSmartPoints: state => state.cachedFilteredSearchedClosestSmartPoints,
  getCustomerPostalCode: (state, getters) => {
    return getters.previewOrderData?.customerData?.postal
  },
  getSelectedSmartPoint: state => state.selectedSmartPoint,
  getPreviousPostalCodeInput: state => state.previousPostalCodeInput,
  getSelectedTransporterSmartPointsApiError: (state, getters) => {
    const transporterClassName = getters.getSelectedTransporterClassName
    const data = getters.getAllTransporterSmartPoints[transporterClassName]
    if (data === undefined) {
      return false
    }
    if (data.responseError) {
      return true
    }
  },
  getSelectedTransporterSmartPointsApiErrorMessage: (state, getters) => {
    const transporterClassName = getters.getSelectedTransporterClassName
    const data = getters.getAllTransporterSmartPoints[transporterClassName]
    if (data === undefined) {
      return ''
    }
    if (getters.getSelectedTransporterSmartPointsApiError) {
      return data.responseError
    }
    return ''
  },
  getSelectedTransporterObject: (state, getters) => {
    const transporterClassName = getters.getSelectedTransporterClassName
    const transporterId = getters.getSelectedTransporterId
    const allAvailableTransporters = getters.getTransporters
    return find(allAvailableTransporters, (transporter) => {
      return transporter.class_name === transporterClassName && Number(transporter.transporter_id) === Number(transporterId)
    })
  },
  urlData: () => {
    return window.advAppData.url
  },
  googleMapsApiKey: (state) => {
    return state.config.googleMapsApiKey
  },
  googleService: (state) => {
    return state.googleService
  },
  getGoogleMapLoading: state => state.googleMapLoading,
  getMapPoints: state => state.mapPoints,
  getCurrentUserGeolocation: (state) => {
    if (state.currentUserGeolocation === '') {
      return state.config.googleMapsDefaultLocationCenter
    }
    return state.currentUserGeolocation
  },
  getCurrentUserGeolocationError: state => state.currentUserGeolocationError,
  getCheckoutLocalErrors: state => state.checkoutLocalErrors,
  getVatError: state => state.vatError,
  getSelectedCurrency: state => state.selectedCurrency,
  afterAddToCartRecommendationProducts: state => state.afterAddToCartRecommendation.products,
  afterAddToCartRecommendationOpenModal: state => state.afterAddToCartRecommendation.openModal,
  afterAddToCartRecommendationRequest: state => state.afterAddToCartRecommendation.requestRecommendation,
  getActiveTagFilters: state => state.activeTagFilters,
  getTagRecordsUsed: state => state.tagRecordsUsed,
  getFilterQueryStringVar: state => state.filterQueryStringVar,
  getListingTagGroupCombos: state => Object.values(state.listingTagGroupCombos).map((item) => item),
  getActiveTagFiltersString (state, getters) {
    if (getters.getActiveTagFilters.length <= 0) {
      return ''
    }
    return `${getters.getFilterQueryStringVar}[]=${getters.getActiveTagFilters.join(`&${getters.getFilterQueryStringVar}[]=`)}`
  },
  getUrlNoFilters (state, getters) {
    const url = '/' + getters.config?.url?.lang + getters.getUriWithoutOffset
    if (getters.getIsTopLevelCategory) {
      return url + '/1'
    }
    return url
  },
  getExternalTransporterRates: state => state.externalTransporterRates,
  getExternalTransporterRatesLoading: state => state.externalTransporterRatesLoading,
  getIsExternalTransporterCost: (state, getters) => (transporterId) => {
    const transporter = find(getters.getTransporters, (transporter) => {
      return Number(transporter.transporter_id) === Number(transporterId)
    })
    return transporter?.eshop_calculated_cost === '0'
  },
  getSelectedTransporterExternalRate: state => state.selectedTransporterExternalRate,
  getPriceUsingCurrency: (state) => (price) => {
    return (price * state.selectedCurrency.rate).toFixed(2)
  },
  isCustomerLoggedIn: (state) => state.isCustomerLoggedIn,
  getGiftPackaging: state => state.giftPackaging,
  getGiftPackagingMessage: state => state.giftPackagingMessage,
  klarnaPaymentAuthorizationGetter: state => state.klarnaPaymentAuthorization,
  klarnaPaymentSessionGetter: state => state.klarnaPaymentSession
}
